/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import React from 'react';
import {AppBar, Box, IconButton, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/esm/Close';
import BackIcon from '@mui/icons-material/esm/ArrowBack';
import PropTypes from 'prop-types';
import { graphManager } from '../../GraphManager';
import HelpIcon from '@mui/icons-material/esm/HelpOutline';

export default function SettingsPaperComponent(props) {
	const handleClose = () => {
		props.close();
		setTimeout(() => {
			window.dispatchEvent(new Event('resize'));
			graphManager.redraw();
		}, 0);
	};

	if (!props.open) {
		return null;
	}
	const scrollable = props.scrollable === undefined ? true : props.scrollable;

	const onHelp = () => {
		const context = props.helpContext || 'introduction';
		window.open(`https://docs.cedalo.com/streamsheets/${context}/`, '_blank');
	};

	return (
		<Box
			sx={{
				width: props.width,
				height: '100%',
				overflowX: 'hidden',
				overflowY: scrollable ? 'auto' : 'hidden',
				// marginRight: '16px',
				borderLeft: (theme) => (theme.palette.mode === 'dark' ? '1px solid #47525d' : '1px solid #ececef'),
				paddingLeft: '8px',
				paddingRight: '8px',
				marginLeft: '8px',
				marginRight: '8px',
				...props.style
			}}
			data-tour={props.tourStep || "settings-sidepanel"}
		>
			<AppBar
				elevation={0}
				sx={{
					backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#212121' : 'white',
					width: '100%',
					height: '37px',
					display: 'flex',
					flexDirection: 'row',
					position: 'relative',
					justifyContent: 'space-between',
					borderBottom: (theme) => (theme.palette.mode === 'dark' ? '1px solid #47525d' : '1px solid #ececef'),
				}}
			>
				<Typography
					sx={{
						padding: '10px 0px 10px 8px',
						display: 'inline-block',
						fontSize: '11pt',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						maxWidth: '70%',
						color: (theme) => (theme.palette.mode === 'dark' ? 'white' : '#222222')
					}}
				>
					{props.title}
				</Typography>
				<div>
					<IconButton
						style={{
							display: 'inline',
							color: (theme) => (theme.palette.mode === 'dark' ? 'white' : '#222222'),
							padding: '8px'
						}}
						onClick={() => onHelp()}
						size="small"
					>
						<HelpIcon />
					</IconButton>
					{props.back ? (
						<IconButton
							style={{
								display: 'inline',
								color: (theme) => (theme.palette.mode === 'dark' ? 'white' : '#222222')
							}}
							onClick={() => props.back()}
							size="small"
						>
							<BackIcon />
						</IconButton>
					) : null}
					{props.close ?
					<IconButton
						style={{
							display: 'inline',
							color: (theme) => (theme.palette.mode === 'dark' ? 'white' : '#222222')
						}}
						onClick={() => handleClose()}
						size="small"
					>
						<CloseIcon fontSize="inherit" />
					</IconButton> : null}
				</div>
			</AppBar>
			<div
				style={{
					position: 'relative',
					margin: '8px',
					height: !scrollable ? 'calc(100% - 56px)' : undefined
				}}
			>
				{props.children}
			</div>
		</Box>
	);
}

SettingsPaperComponent.propTypes = {
	open: PropTypes.bool.isRequired,
	close: PropTypes.func,
	width: PropTypes.string,
	title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired
};

SettingsPaperComponent.defaultProps = {
	close: undefined,
	width: '300px',
};
